import React from 'react'
import {
  Typography as T,
  LinkProps,
  ParagraphProps,
  TextProps,
  TitleProps,
} from 'src/antd'

import {
  TVariant,
  variants,
  TFontWeight,
  fontWeights,
  loadingStylesMap,
  TLoadingSizes,
} from './config'
import { StyledSkeletonInput } from '../Skeleton'

export type { TFontWeight, TVariant } from './config'

const { Link, Paragraph, Text, Title } = T

const components = {
  Text,
  Title,
  Paragraph,
  Link,
}

type TTextComponent = {
  component?: 'Text'
} & TextProps

type TTitleComponent = {
  component?: 'Title'
} & TitleProps

type TParagraphComponent = {
  component?: 'Paragraph'
} & ParagraphProps

type TLinkComponent = {
  component?: 'Link'
} & LinkProps

export type TTypographyProps = {
  variant?: TVariant
  weight?: TFontWeight
  loading?: boolean
  loadingSize?: TLoadingSizes
} & (TTextComponent | TTitleComponent | TParagraphComponent | TLinkComponent)

/**
 * `forwardRef` rather than a bare `FC`, and it has to be: this component is a
 * direct Tooltip child at several call sites, and the shadcn Tooltip binds its
 * trigger with Radix `asChild`, which uses the child's ref as the popper
 * anchor. A function component that swallows the ref loses the anchor and logs
 * "Function components cannot be given refs". Nothing about the rendered markup
 * changes, so none of the ~430 call sites move.
 *
 * The `loading` branch is the one place that cannot simply pass the ref on:
 * antd's `Skeleton.Input` is a bare function component, so handing it a ref
 * attaches nothing and logs that same warning. It gets a host `<span>` to
 * carry both halves of a trigger instead — the ref Radix anchors against AND
 * the pointer/focus handlers and `aria-describedby` its Slot merges onto the
 * cloned element, which is why `props` is spread there. An anchor without the
 * handlers is an inert trigger: the bubble would never open.
 *
 * That wrapper is emitted ONLY when a ref was actually asked for, and the
 * ref-presence condition is DELIBERATE, not an oversight. Wrapping every
 * child wholesale is the S6 shape that collapsed the settings integrations
 * grid; conditioning on a ref keeps it to the one case that is already broken
 * without it, so the ~430 ordinary call sites carry no layout risk at all. Do
 * not "simplify" the condition away.
 */
export const Typography = React.forwardRef<HTMLElement, TTypographyProps>(
  (
    {
      component = 'Text',
      variant = 'body-14',
      weight = 'regular',
      children,
      loading,
      loadingSize = 'default',
      ...props
    },
    ref
  ) => {
    /**
     * The default CLAMPS in both modes — losing single-line truncation on
     * these ~430 call sites would move layout across the app. The tooltip
     * half of the default is now the SAME in both modes too: `src/antd/
     * Typography.shadcn` measures real overflow (`scrollWidth`/`scrollHeight`
     * vs. `clientWidth`/`clientHeight`, `ResizeObserver`-backed) and mounts
     * its Tooltip only when the text is actually clipped, so `{ tooltip:
     * children }` costs nothing on text that fits in either mode — an
     * always-on antd Tooltip per label is no longer what this default buys.
     * An explicit `ellipsis` from a call site is honoured unchanged in both
     * modes, as before.
     */
    const getEllipsisProp = () => {
      if (component === 'Text' || component === 'Link') {
        return props.ellipsis ?? { tooltip: children }
      }
      return props.ellipsis
    }

    const Component = components[component]

    if (loading) {
      const skeleton = (
        <StyledSkeletonInput
          size="default"
          width={loadingStylesMap[loadingSize].width}
          style={{
            height: variants[variant].fontSize,
            lineHeight: variants[variant].lineHeight,
          }}
          active
        />
      )

      if (!ref) return skeleton

      return (
        <span
          {...(props as React.HTMLAttributes<HTMLSpanElement>)}
          ref={ref as React.Ref<HTMLSpanElement>}
          style={{ display: 'inline-flex' }}
        >
          {skeleton}
        </span>
      )
    }

    return (
      <Component
        {...props}
        ref={ref as never}
        // @ts-expect-error ellipsis is boolean for link and node for text
        ellipsis={getEllipsisProp()}
        style={{ ...variants[variant], ...fontWeights[weight], ...props.style }}
      >
        {children}
      </Component>
    )
  }
)

Typography.displayName = 'Typography'
